import * as React from 'react';
import { cn } from '@/lib/utils';
import { AITextHelper } from '@/components/ai/AITextHelper';
import { VoiceInputButton } from '@/components/voice/VoiceInputButton';

export interface AITextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  onValueChange?: (value: string) => void;
  aiContext?: string;
  aiFieldLabel?: string;
  aiDisabled?: boolean;
  voiceInput?: boolean;
}

const AITextarea = React.forwardRef<HTMLTextAreaElement, AITextareaProps>(
  (
    {
      className,
      value,
      onChange,
      onValueChange,
      aiContext,
      aiFieldLabel,
      aiDisabled,
      disabled,
      voiceInput = true,
      ...props
    },
    ref
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const mergedRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      },
      [ref]
    );

    const handleValueChange = (newValue: string) => {
      if (onValueChange) {
        onValueChange(newValue);
      } else {
        const event = {
          target: { value: newValue },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(event);
      }
    };

    const pushVoice = (newValue: string) => {
      handleValueChange(newValue);
    };

    const showVoice = voiceInput && disabled !== true;

    return (
      <div className="relative w-full min-w-0">
        <textarea
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={cn(
            'flex min-h-[120px] w-full rounded-md border border-border bg-card px-3 py-2 pr-14 text-sm shadow-sm transition-colors placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground',
            className
          )}
          ref={mergedRef}
          {...props}
        />
        {showVoice && (
          <VoiceInputButton
            inputRef={innerRef}
            value={value}
            onValueChange={pushVoice}
            disabled={disabled}
            placement="textarea"
            pairedWithAi
          />
        )}
        <div className="pointer-events-none absolute inset-0 min-h-[120px]">
          <AITextHelper
            value={value}
            onValueChange={handleValueChange}
            context={aiContext}
            fieldLabel={aiFieldLabel}
            disabled={aiDisabled ?? disabled}
            placement="textarea"
            className="h-full w-full min-w-0"
          />
        </div>
      </div>
    );
  }
);
AITextarea.displayName = 'AITextarea';

export { AITextarea };
